// Язык интерфейса нового ученика (B4, 2026-10-20). Регистрация до этой правки не передавала язык,
// профиль создавался с русским по умолчанию, и AppGate после знакомства переключал ученика с
// английским интерфейсом на русский. Теперь язык, выбранный в браузере при регистрации, запоминается
// до первой загрузки профиля: он главнее профильного и сам записывается в профиль (PUT /profile) —
// так работает и со старым бэкендом, который язык из тела регистрации не читает.
//
// Модуль без импортов приложения: его проверяет tests/interface-language.test.ts под Node.

export type UiLang = 'ru' | 'en';
/** Значение `language_interface` в API профиля. */
export type ProfileLanguage = 'Russian' | 'English';

export const PENDING_LANGUAGE_KEY = 'lx.lang.pending';
/** Синхронизация обычно проходит при первой же загрузке профиля; старее — уже не «только что». */
export const PENDING_LANGUAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** 'en', 'en-GB' → 'en'; всё остальное — 'ru' (основной язык приложения). */
export function uiLang(value: string | null | undefined): UiLang {
  return value?.toLowerCase().startsWith('en') ? 'en' : 'ru';
}

export const profileLanguage = (lang: UiLang): ProfileLanguage => (lang === 'en' ? 'English' : 'Russian');

export const langFromProfile = (value: ProfileLanguage | string | null | undefined): UiLang =>
  value === 'English' ? 'en' : 'ru';

function localStore(): KeyValueStore | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null; // хранилище заблокировано настройками приватности
  }
}

/** После регистрации: этот язык ученик выбрал сам, профиль о нём ещё не знает. */
export function rememberPendingLanguage(
  lang: UiLang,
  userId: string,
  store: KeyValueStore | null = localStore(),
  now: number = Date.now(),
): void {
  try {
    store?.setItem(PENDING_LANGUAGE_KEY, JSON.stringify({ lang, userId, at: now }));
  } catch {
    // нет места — останется прежнее поведение: язык из профиля
  }
}

/** Язык, ждущий записи в профиль этого ученика; чужой или устаревший — стирается. */
export function readPendingLanguage(
  userId: string | null | undefined,
  store: KeyValueStore | null = localStore(),
  now: number = Date.now(),
): UiLang | null {
  if (!store) return null;
  try {
    const raw = store.getItem(PENDING_LANGUAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const record = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    const { lang, at } = record;
    const fresh = typeof at === 'number' && now - at <= PENDING_LANGUAGE_TTL_MS && at - now <= 60_000;
    if ((lang !== 'ru' && lang !== 'en') || !fresh) {
      store.removeItem(PENDING_LANGUAGE_KEY);
      return null;
    }
    // Другой аккаунт в этом браузере: его профиль не трогаем, запись оставляем владельцу.
    if (record.userId !== (userId ?? '')) return null;
    return lang;
  } catch {
    return null;
  }
}

export function clearPendingLanguage(store: KeyValueStore | null = localStore()): void {
  try {
    store?.removeItem(PENDING_LANGUAGE_KEY);
  } catch {
    // нечего чистить
  }
}

export interface LanguageDecision {
  /** На какой язык переключить интерфейс; null — уже на нём. */
  apply: UiLang | null;
  /** Что записать в профиль (PUT /profile); null — профиль уже верный. */
  push: ProfileLanguage | null;
  /** Профиль совпал с выбранным при регистрации — запись больше не нужна. */
  clearPending: boolean;
  /** Язык, который в итоге показан, — для стора (`user.lang`). */
  shown: UiLang;
}

/**
 * Что делать с языком, когда пришёл профиль. Без отложенного выбора — язык профиля (новое
 * устройство, где `lx.lang` ещё не выставлен). С ним — выбранный при регистрации, а профиль догоняет.
 */
export function decideInterfaceLanguage(input: {
  profile: ProfileLanguage | string | null | undefined;
  pending: UiLang | null;
  current: UiLang;
}): LanguageDecision {
  const fromProfile = langFromProfile(input.profile);
  const target = input.pending ?? fromProfile;
  return {
    apply: input.current === target ? null : target,
    push: input.pending && input.pending !== fromProfile ? profileLanguage(input.pending) : null,
    clearPending: !!input.pending && input.pending === fromProfile,
    shown: target,
  };
}
